/**
 * Pure formatting helpers for the asset gallery. The hooks layer handles
 * fetching; these helpers turn DTO fields into the strings the gallery
 * renders.
 */

// Asset-URL helpers live in the neutral utils layer (shared with Image Lab);
// re-exported here so existing asset-gallery import sites stay unchanged.
export { assetFileUrl, resolveAssetUrl } from '../../utils/assetUrl';

import type { AssetFolder } from '../../domain';

/**
 * Product-level display overrides, applied before the CamelCase split.
 *
 * The wire identifiers are backend contracts, so `CreativeToolboxApp` stays
 * exactly as the API sends it; only the label a person reads carries the
 * product's settled name. Lab names ("ImageLab", "WriterLab") are *not*
 * product names and keep splitting normally.
 */
const APP_TYPE_DISPLAY_OVERRIDES: Readonly<Record<string, string>> = {
  CreativeToolboxApp: 'Coral Creator',
};

/** Own-key lookup, so an appAgentType like `constructor` or `toString` can't
 *  reach Object.prototype and return a function where a label is expected. */
function displayOverride(appAgentType: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(APP_TYPE_DISPLAY_OVERRIDES, appAgentType)
    ? APP_TYPE_DISPLAY_OVERRIDES[appAgentType]
    : undefined;
}

/**
 * Humanize an app-agent-type identifier into a display label.
 * "ImageLab" -> "Image Lab"
 * "WriterLab" -> "Writer Lab"
 * "FashionStudio" -> "Fashion Studio"
 * "CreativeToolboxApp" -> "Coral Creator" (product-name override)
 * Falls back to the input unchanged when it doesn't match a CamelCase shape.
 */
export function humanizeAppType(appAgentType: string): string {
  if (!appAgentType) return appAgentType;
  const override = displayOverride(appAgentType);
  if (override) return override;
  // Split on lowercase-uppercase boundaries: "ImageLab" -> ["Image", "Lab"]
  const split = appAgentType.replace(/([a-z])([A-Z])/g, '$1 $2');
  // Capitalize each word's first letter (preserves multi-cap acronyms like "AI").
  return split
    .split(' ')
    .map(w => (w.length === 0 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * Format a byte count into a 1-decimal-place "X.Y UNIT" string. Stops at
 * GB; assets that big should be unusual.
 *
 * - 0 -> "0 B"
 * - 1024 -> "1.0 KB"
 * - 1_500_000 -> "1.4 MB"
 */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'] as const;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  // Bytes don't need a decimal (they're integers anyway); KB+ shows 1 dp.
  return unit === 0 ? `${Math.round(value)} B` : `${value.toFixed(1)} ${units[unit]}`;
}

/**
 * Format width × height into a "WxH" string with the multiplication sign.
 * Returns an empty string when either dim is non-positive.
 */
export function formatDimensions(width?: number, height?: number): string {
  if (!width || !height || width <= 0 || height <= 0) return '';
  return `${Math.round(width)} × ${Math.round(height)}`;
}

/**
 * Filter a folder hierarchy by a search query. Matches against
 * appAgentType (humanized) + each folder's sessionName. Returns a new
 * hierarchy with only the apps whose name OR any session matches; within
 * a matching app, only the matching sessions are kept.
 *
 * Empty / whitespace query returns the hierarchy unchanged.
 */
export interface SimpleFolder {
  id: string;
  sessionId: string;
  sessionName?: string;
  assetCount: number;
}

export interface SimpleAppFolder {
  appAgentType: string;
  name: string;
  folders: SimpleFolder[];
  assetCount: number;
}

export function filterAppFolders<A extends SimpleAppFolder>(
  apps: A[],
  query: string,
): A[] {
  const q = query.trim().toLowerCase();
  if (!q) return apps;
  const out: A[] = [];
  for (const app of apps) {
    // Match the humanized label too, not just the wire values. The panel
    // renders `humanizeAppType(appAgentType)`, so without this a user can read
    // "Coral Creator" (or "Image Lab") on screen and have the search box tell
    // them it doesn't exist — the raw ids are `CreativeToolboxApp` and
    // `ImageLab`, neither of which contains the words as typed.
    const appMatches =
      app.name.toLowerCase().includes(q) ||
      app.appAgentType.toLowerCase().includes(q) ||
      humanizeAppType(app.appAgentType).toLowerCase().includes(q);
    const matchedFolders = app.folders.filter(f =>
      (f.sessionName ?? '').toLowerCase().includes(q) || f.sessionId.toLowerCase().includes(q),
    );
    if (appMatches) {
      // If the app itself matches, keep all its folders.
      out.push(app);
    } else if (matchedFolders.length > 0) {
      out.push({ ...app, folders: matchedFolders } as A);
    }
  }
  return out;
}

/**
 * A synthetic "All assets" folder for one app. The folder hierarchy groups by
 * session and omits session-less assets — notably imports from other SmartAI
 * products — so this is the only folder they appear in. An empty `sessionId`
 * means "no session filter": the list returns every asset of the app.
 */
export function allAppAssetsFolder(appAgentType: string, assetCount = 0): AssetFolder {
  return {
    id: `${appAgentType}/*`,
    appAgentType,
    sessionId: '',
    sessionName: 'All assets',
    assetCount,
    updatedAt: '',
  };
}
